import { CategoryAndTag } from "api/generated/model";
import { TagsCascader } from "components/table/tagsFilter/tagsCascader";
import { TagCascaderOption } from "components/table/tagsFilter/types";

import { TagCategory } from "./types";

interface TagsFilterProps {
    placeholder?: string;
    tagCategories: TagCategory[];
    value: CategoryAndTag[];
    onChange: (value: CategoryAndTag[]) => void;
}

// TagCascaderOption always has string values
type TagCascaderValue = string[];

function isValidCascaderValue(
    value: (string | number)[]
): value is TagCascaderValue {
    return value.every((item): item is string => typeof item === "string");
}

export function TagsFilter({
    placeholder,
    tagCategories,
    value,
    onChange,
}: TagsFilterProps) {
    const options = buildTagCascaderOptions(tagCategories);
    const cascaderValue = transformTagNameSelectionsToCascaderValue(value);

    const handleChange = (cascaderValue: (string | number)[][]) => {
        const tagSelections: CategoryAndTag[] = cascaderValue.reduce(
            (acc: CategoryAndTag[], value: (string | number)[]) => {
                if (!isValidCascaderValue(value)) {
                    console.error("Invalid cascader value received:", value);
                    return acc;
                }

                if (value.length === 1) {
                    const categoryName = value[0];
                    const category = tagCategories.find(
                        (cat) => cat.name === categoryName
                    );
                    if (category) {
                        const categoryTags = category.tags.map(
                            (tagName: string) => ({
                                categoryName,
                                tagName,
                            })
                        );
                        return [...acc, ...categoryTags];
                    }
                } else if (value.length === 2) {
                    const categoryName = value[0];
                    const tagName = value[1];
                    return [...acc, { categoryName, tagName }];
                }
                return acc;
            },
            []
        );
        onChange(tagSelections);
    };

    return (
        <TagsCascader
            placeholder={placeholder}
            options={options}
            onChange={handleChange}
            value={cascaderValue}
        />
    );
}

const collator = new Intl.Collator();
function buildTagCascaderOptions(
    tagCategories: TagCategory[]
): TagCascaderOption[] {
    const categories = tagCategories.map((category) => ({
        value: category.name,
        label: category.name,
        children: category.tags.map((tagName: string) => ({
            value: tagName,
            label: tagName,
        })),
    }));

    const sortedCategories = categories
        .map((category) => {
            const sortedTags = [...category.children].sort((a, b) =>
                collator.compare(a.label, b.label)
            );
            return {
                ...category,
                children: sortedTags,
            };
        })
        .sort((a, b) => collator.compare(a.label, b.label));

    return sortedCategories;
}

function transformTagNameSelectionsToCascaderValue(
    tagSelections: CategoryAndTag[]
): string[][] {
    const cascaderValue: string[][] = [];

    tagSelections.forEach(({ categoryName, tagName }) => {
        cascaderValue.push([categoryName, tagName]);
    });

    return cascaderValue;
}
